import { Settings2 } from 'lucide-react';
import { IPostDetailResponse } from '@/redux/post/post.d';
import {
  SpecCard,
  SpecCaption,
  SpecRow,
  SpecLink,
  SpecLinks,
} from './specCardParts';
import StoreIcon, { storeHostOf } from '@/components/common/StoreIcon';

// Complexity is inherent to the spec table: one branch per optional job field.
// Carried in from in-flight work on this card, not introduced by the
// makes/comments redesign this commit is about — left for that author to split.
// fallow-ignore-next-line complexity
function JobSettingsCard({ post }: { post: IPostDetailResponse }) {
  const software = post.softwareUsed?.filter(Boolean) ?? [];
  const isDim = Boolean(post.isDimensionalMaterial);
  const materialLinks =
    (isDim && post.dimensionalMaterialLinks?.filter(Boolean)) || [];

  // Label each link by host; number them when a host repeats so they stay distinct.
  const linkHosts = post.materialLinkHosts;
  const hostCounts = materialLinks.reduce<Record<string, number>>(
    (acc, url) => {
      const host = storeHostOf(url, linkHosts);
      acc[host] = (acc[host] || 0) + 1;
      return acc;
    },
    {}
  );
  const seenHosts: Record<string, number> = {};
  const materialLinkLabels = materialLinks.map((url) => {
    const host = storeHostOf(url, linkHosts);
    if (hostCounts[host] <= 1) return host;
    seenHosts[host] = (seenHosts[host] || 0) + 1;
    return `${host} (${seenHosts[host]})`;
  });

  const hasMaterialSection =
    software.length > 0 || post.materialUsed || materialLinks.length > 0;

  if (!post.zZeroPosition && !post.xyDatumPosition && !hasMaterialSection) {
    return null;
  }

  return (
    <SpecCard
      icon={<Settings2 className="h-[18px] w-[18px]" />}
      title="Job Settings"
    >
      {(post.zZeroPosition || post.xyDatumPosition) && (
        <SpecCaption>Positioning</SpecCaption>
      )}
      {post.zZeroPosition && (
        <SpecRow label="Z Zero Position">{post.zZeroPosition}</SpecRow>
      )}
      {post.xyDatumPosition && (
        <SpecRow label="XY Datum Position">{post.xyDatumPosition}</SpecRow>
      )}

      {hasMaterialSection && <SpecCaption>Software &amp; Material</SpecCaption>}
      {software.length > 0 && (
        <SpecRow label="Software">
          <SpecLinks>
            {software.map((name) => (
              <SpecLink
                key={name}
                href={`https://www.onefinitycnc.com/search?q=${name}`}
              >
                {name}
              </SpecLink>
            ))}
          </SpecLinks>
        </SpecRow>
      )}
      {post.materialUsed && (
        <SpecRow label="Material">{post.materialUsed}</SpecRow>
      )}
      {materialLinks.length > 0 && (
        <SpecRow label="Big Box Store Lumber">
          <SpecLinks>
            {materialLinks.map((url, i) => (
              <SpecLink
                key={url}
                href={url}
                icon={
                  <StoreIcon
                    url={url}
                    host={storeHostOf(url, linkHosts)}
                    className="h-6 w-6"
                  />
                }
              >
                {materialLinkLabels[i]}
              </SpecLink>
            ))}
          </SpecLinks>
        </SpecRow>
      )}
    </SpecCard>
  );
}

export default JobSettingsCard;
